import { observable, runInAction } from 'mobx';
import RootStore from './RootStore';
import BaseLoginStore from './BaseLoginStore';
import { API } from '../util/ApiUtil';
import { filterForwardParamsFromCurrentParams } from '../util/ForwardParamsUtil';
import { AxiosError } from 'axios';
import { UiResponse } from '../CommonInterfaces';
import ErrorUtil from '../util/ErrorUtil';
import GaDataStore from './GaDataStore';
import '../util/WebEidMessageGuard';
import { authenticate, status } from '@web-eid/web-eid-library/web-eid';

export default class IdCardLoginStore extends BaseLoginStore {
  @observable accessor retryExhausted: boolean = false;

  constructor(rootStore: RootStore) {
    super(rootStore);
  }

  public handleLogin = async () => {
    runInAction(() => {
      this.submitting = true;
    });
    try {
      await status();
    } catch (_e) {
      runInAction(() => {
        this.submitting = false;
      });
      this.showUpdateSoftwareNotice();
      return;
    }
    let authToken: Awaited<ReturnType<typeof authenticate>>;
    try {
      const challengeResponse = await API.get('/id-card/auth/challenge');
      try {
        authToken = await authenticate(challengeResponse.data.nonce, { lang: 'et' });
      } catch (webEidError) {
        return this.handleWebEidError(webEidError);
      }
      const loginResponse = await API.post(`/id-card/v2/auth${this.getLoginParams()}`, { authToken });
      await this.handleSuccess(loginResponse, false, 'idcard');
    } catch (e) {
      await this.handleErrorAndNetworkError(e as AxiosError<UiResponse>);
    }
  };

  private handleWebEidError = (webEidError: unknown) => {
    runInAction(() => {
      this.submitting = false;
    });
    ErrorUtil.pushErrorToTeliaRumAndLog(webEidError as Error);
    this.uiStore.showErrorNotice('idcard.error');
    GaDataStore.loginStatus(false, 'idcard', this.rememberMeStore.rememberMe);
  };

  private showUpdateSoftwareNotice = () => {
    this.uiStore.showErrorNotice('updateSoftwareNotice');
  };

  private getLoginParams() {
    return `${filterForwardParamsFromCurrentParams({ ...this.routeStore.decodedParams, rememberMe: this.rememberMeStore.rememberMe })}`;
  }

  handleErrorAndNetworkError = async (e: AxiosError<UiResponse>): Promise<void> => {
    if (!this.retryExhausted && e.message === 'Network Error') {
      runInAction(() => {
        this.retryExhausted = true;
      });
      return this.handleLogin();
    }
    return this.handleError(e, 'idcard');
  };
}
